import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const Contact = () => {
  const { lang } = useLang();
  const fr = content.contact.fr;
  const en = content.contact.en;
  const isBoth = lang === "both";
  const data = lang === "en" ? en : fr;

  return (
    <section id="contact" data-testid="contact-section" className="bg-white section">
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="lg:col-span-5 flex flex-col gap-10"
          >
            <div>
              <span className="eyebrow-yellow">
                <MapPin size={12} strokeWidth={2.5} />
                <span>{isBoth ? `${fr.eyebrow} · ${en.eyebrow}` : data.eyebrow}</span>
              </span>

              <h2
                data-testid="contact-title"
                className="mt-5 h-display text-[36px] sm:text-[44px] md:text-[60px] leading-[0.95]"
              >
                <span className="block">{data.title}</span>
                <span className="block italic">{data.titleAccent}</span>
              </h2>
              <p className="mt-6 text-base text-black/70 max-w-md leading-relaxed">
                {data.sub}
              </p>
            </div>

            <div className="space-y-7">
              <div data-testid="contact-address" className="bb-card p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-black/55 mb-2 flex items-center gap-2">
                  <MapPin size={12} strokeWidth={2.5} />
                  <span>{isBoth ? `${fr.addressLabel} · ${en.addressLabel}` : data.addressLabel}</span>
                </div>
                <p className="font-display text-xl md:text-2xl leading-snug uppercase">
                  {content.contactInfo.addressLines[0]}
                </p>
                <p className="font-display text-xl md:text-2xl leading-snug uppercase">
                  {content.contactInfo.addressLines[1]}
                </p>
                <a
                  href={content.contactInfo.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-link"
                  className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] text-black underline underline-offset-4 decoration-2 decoration-[var(--bb-yellow)] hover:decoration-black"
                >
                  <span>{isBoth ? `${fr.directions} · ${en.directions}` : data.directions}</span>
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
              </div>

              <div data-testid="contact-phone" className="bb-card p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-black/55 mb-2 flex items-center gap-2">
                  <Phone size={12} strokeWidth={2.5} />
                  <span>{isBoth ? `${fr.phoneLabel} · ${en.phoneLabel}` : data.phoneLabel}</span>
                </div>
                <a
                  href={content.contactInfo.phoneHref}
                  className="font-display text-2xl md:text-3xl leading-snug hover:text-black/60 transition-colors"
                >
                  {content.contactInfo.phone}
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div
              data-testid="contact-map"
              className="bb-frame border-[3px] border-black aspect-[4/3] md:aspect-[16/11] w-full overflow-hidden"
            >
              <iframe
                title="Big Boss Snack Bar — Map"
                src={content.contactInfo.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
